/**
 * arViewerUrl — where the AR viewer lives, and the URLs that address it.
 *
 * Two entry points into the same deployed app:
 *   ?s=<sessionId>    a LIVE session (the AR button's QR): one owner's sketch, re-run on every Run.
 *   ?g=<classroomId>  a PUBLISHED classroom: frozen snapshots with a picker, fully public, no login.
 *
 * Shared by ARButton.jsx and PublishClassroomModal.jsx.
 */

import { getConfig } from './getConfig';

// The AR viewer's Vite dev server pins this port (ar-viewer/vite.config.js, strictPort).
// Keep in sync if that changes.
const AR_VIEWER_DEV_PORT = 5180;

/**
 * Resolve the AR viewer origin. In dev the viewer runs as its own Vite dev server on the same host at
 * port 5180 (started alongside the editor by `yarn dev`), so derive it from the current origin — this
 * reaches it from both localhost and the LAN IP (phone) with no rebuild, the same same-origin reasoning
 * that keeps EDITOR_URL/PREVIEW_URL unset in dev. In prod, use the baked AR_VIEWER_URL (the Firebase
 * Hosting site).
 */
export function arViewerBase() {
  const isProd = getConfig('NODE_ENV') === 'production';
  if (!isProd && typeof window !== 'undefined' && window.location?.hostname) {
    const { protocol, hostname } = window.location;
    return `${protocol}//${hostname}:${AR_VIEWER_DEV_PORT}`;
  }
  return getConfig('AR_VIEWER_URL', { warn: false }) || '';
}

/** Strip a trailing slash so a URL built on top of a base never contains `//`. */
function trimBase(base) {
  return (base || '').replace(/\/$/, '');
}

/**
 * Live-session viewer URL: `${base}/?s=<sessionId>`, with the login param appended only when
 * explicitly allowed.
 */
export function buildArViewerUrl(base, sessionId, allowLogin) {
  const url = `${trimBase(base)}/?s=${sessionId}`;
  return allowLogin ? `${url}&login=1` : url;
}

/**
 * Published-classroom URL: `${base}/?g=<classroomId>`, optionally deep-linking one sketch with
 * `&i=<projectId>`. Never carries `login=1`: the published app is public and read-only, and its code
 * path never touches Firebase Auth.
 */
export function buildPublicationUrl(base, classroomId, projectId) {
  if (!base || !classroomId) return '';
  const url = `${trimBase(base)}/?g=${classroomId}`;
  return projectId ? `${url}&i=${projectId}` : url;
}
